import { useMemo } from "react";
import type { NovelSummary } from "../apiClient.js";
import { pickContinueReading } from "../lib/continueReading.js";
import { useT } from "../lib/i18n/store.js";
import { POSTER_ASPECT, POSTER_CSS_WIDTH, fallBackToSource, sizedImageUrl } from "../lib/imageUrl.js";
import { fallbackCoverUrl } from "../lib/novel.js";

/**
 * "Continue reading": the stories this browser has an unfinished run in,
 * newest first (lib/continueReading.ts), each one click from where the reader
 * left off — the player restores the saved place by itself. Renders nothing
 * when there is no such run, so a first-time visitor never sees an empty shelf.
 *
 * `novels` is a list the page already has (the public catalog): a run is shown
 * only when its novel is in it.
 */
export function ContinueReadingShelf({
  novels,
  onPlay,
  className
}: {
  novels: readonly NovelSummary[];
  onPlay: (novelId: string) => void;
  className?: string;
}) {
  const t = useT();
  // Read once per list: the page remounts when the reader comes back from the
  // player, so a fresh run is picked up without watching storage.
  const shelf = useMemo(() => pickContinueReading(novels), [novels]);
  if (shelf.length === 0) return null;

  return (
    <section
      className={className ? `continue-shelf ${className}` : "continue-shelf"}
      aria-labelledby="continue-shelf-heading"
    >
      <h2 id="continue-shelf-heading" className="continue-shelf-heading">
        {t("continue.heading")}
      </h2>
      <ul className="continue-shelf-list">
        {shelf.map(({ novel }) => (
          <li key={novel.id}>
            {/* Its name is its visible text, "‹title› Continue", in every
                language (the cover is decorative, the arrow hidden). */}
            <button type="button" className="continue-card" onClick={() => onPlay(novel.id)}>
              <img
                className="continue-card-cover"
                // The cover variant every card shares, so it is usually cached already.
                src={sizedImageUrl(novel.cover_asset_url, POSTER_CSS_WIDTH, { aspect: POSTER_ASPECT }) ?? fallbackCoverUrl}
                onError={fallBackToSource(novel.cover_asset_url)}
                alt=""
                loading="lazy"
                decoding="async"
              />
              <span className="continue-card-text">
                <span className="continue-card-title">{novel.title}</span>{" "}
                <span className="continue-card-action">
                  {t("continue.resume")}
                  <span aria-hidden="true"> →</span>
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
